/**
 * Service-wide screen-reader announcement on SPA navigation.
 *
 * After every route change, move focus to the page's H1 so screen readers
 * read the heading and the user knows the page changed. Without this, SPA
 * transitions are silent — the URL updates and content swaps but no native
 * announcement fires (no real navigation happened).
 *
 * Skipped when a page-level handler (e.g. `usePageHeadingFocus` inside a
 * sub-view, or the apply page's just-completed-step-row focus) has already
 * moved focus to a more specific element. We detect that by checking
 * `document.activeElement` after letting the page's own mount-time logic
 * run for two ticks. If something was focused, we leave it alone.
 */
export default defineNuxtPlugin(() => {
  const router = useRouter()

  /**
   * Focuses the page heading if no per-page handler has already moved focus
   * to something inside `<main>`. Layout-level focus (navbar links, header
   * controls) is *not* inside `<main>` and gets overridden — that's what
   * makes the screen reader announce the new page on click-through.
   * @param {HTMLElement} heading - The H1 element to focus.
   */
  const focusHeadingIfAppropriate = (heading: HTMLElement) => {
    const main = document.querySelector('main')
    const active = document.activeElement
    const activeInsideMain = !!main && !!active && active !== document.body && main.contains(active)
    if (activeInsideMain) return

    if (!heading.hasAttribute('tabindex')) {
      heading.setAttribute('tabindex', '-1')
    }
    heading.focus()
  }

  router.afterEach(async () => {
    // Two ticks: first to let the new page render, second to let any
    // `await nextTick()`-based focus calls inside the page resolve.
    await nextTick()
    await nextTick()

    const main = document.querySelector('main')
    const heading = (main?.querySelector('h1') ??
      document.querySelector('h1')) as HTMLElement | null

    if (heading) {
      focusHeadingIfAppropriate(heading)
      return
    }

    // The H1 isn't in the DOM yet — typical for `ssr: false` pages that
    // render a loading spinner first and only inject the heading after the
    // initial data fetch. Watch for it to appear and focus then. A safety
    // timeout disconnects the observer to avoid leaks if the user navigates
    // away before the H1 ever shows.
    const observer = new MutationObserver(() => {
      const found = (document.querySelector('main h1') ??
        document.querySelector('h1')) as HTMLElement | null
      if (!found) return
      observer.disconnect()
      clearTimeout(timeoutId)
      focusHeadingIfAppropriate(found)
    })
    observer.observe(document.body, { childList: true, subtree: true })
    const timeoutId = window.setTimeout(() => observer.disconnect(), 4000)
  })
})
